import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceAdvancedStrategy,
  useServiceAdvancedEnabledSites,
  useCreateServiceAdvancedStrategy,
  useUpdateServiceAdvancedStrategy,
  ADVANCED_TYPE_COOKIE,
  type AdvancedStrategy,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';

import { CheckboxField } from '@/pages/services/waap/components/CheckboxField';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './CookieForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;
const COOKIE_MAX = 200;
const TYPE = ADVANCED_TYPE_COOKIE;

/** Action policies. Only "Block" is confirmed from a captured payload. */
const ACTIONS = [
  { value: 'Block', label: 'Block' },
  { value: 'Redirect', label: 'Redirect' },
  { value: 'By-pass', label: 'By-pass' },
] as const;

/** Cookie protection mode (protection_method). Both values confirmed from
 *  captured vendor payloads: signature="sign", encryption="encryption". The BE
 *  passes config through untouched, so these strings must match the vendor. */
const PROTECTION_MODES = [
  { value: 'sign', label: 'Cookie Signature' },
  { value: 'encryption', label: 'Cookie Encryption' },
] as const;

/** The Cookie Protection (type 10) per-type config. */
interface CookieConfig {
  protection_method: string;
  /** Start Time as epoch milliseconds. */
  compatibility_time: number;
  cookie_names: string;
  enable_http_only: boolean;
  enable_secure: boolean;
  enable_ip_check: boolean;
  action: string;
  block_value: number;
  delay: number;
  redirect_value: string;
}

interface FormState extends CookieConfig {
  name: string;
  description: string;
  sites: number[];
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  sites: [],
  protection_method: 'sign',
  compatibility_time: Date.now(),
  cookie_names: '',
  enable_http_only: true,
  enable_secure: false,
  enable_ip_check: false,
  action: 'Block',
  block_value: 418,
  delay: 0,
  redirect_value: '',
});

const pad = (n: number) => String(n).padStart(2, '0');
/** epoch ms → `datetime-local` value (local time, with seconds). */
const toLocalInput = (ms: number): string => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}:${pad(d.getSeconds())}`;
};
/** `datetime-local` value → epoch ms (falls back to now on invalid input). */
const fromLocalInput = (s: string): number => {
  const ms = new Date(s).getTime();
  return Number.isNaN(ms) ? Date.now() : ms;
};

/**
 * Add / Edit a Cookie Protection rule (AI-WAF → Advanced Protection,
 * waf_advanced_strategy type 10). Signs/encrypts cookies from a start time and
 * enforces security attributes. Builds the per-type `config`; the BE adds the
 * common envelope keys. Routes: aiwaf/advanced/cookie/new + /:id.
 */
export function CookieForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;


  const { data: existing, isLoading: loadingExisting } = useServiceAdvancedStrategy<CookieConfig>(
    serviceId,
    TYPE,
    id,
  );
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!existing) return;
    const c = existing.config ?? ({} as CookieConfig);
    setForm({
      name: existing.name ?? '',
      description: existing.description ?? '',
      sites: existing.sites ?? [],
      protection_method: c.protection_method || 'sign',
      compatibility_time: c.compatibility_time || Date.now(),
      cookie_names: c.cookie_names ?? '',
      enable_http_only: c.enable_http_only ?? true,
      enable_secure: c.enable_secure ?? false,
      enable_ip_check: c.enable_ip_check ?? false,
      action: c.action || 'Block',
      block_value: c.block_value ?? 418,
      delay: c.delay ?? 0,
      redirect_value: c.redirect_value ?? '',
    });
  }, [existing]);

  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const create = useCreateServiceAdvancedStrategy<CookieConfig>(serviceId ?? '', TYPE, {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'cookie_protection_rule', 'create');
      show({ tone: 'success', title: 'Cookie Protection rule created' });
      navigate(`${base}/aiwaf/advanced?tab=cookie`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceAdvancedStrategy<CookieConfig>(serviceId ?? '', TYPE, id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'cookie_protection_rule', 'edit');
      show({ tone: 'success', title: 'Cookie Protection rule saved' });
      navigate(`${base}/aiwaf/advanced?tab=cookie`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const cookieValid = form.cookie_names.trim().length > 0;
  const actionValid = form.action !== 'Block' ? true : form.block_value > 0;
  const canSubmit = nameValid && cookieValid && actionValid && !saving;

  const submit = () => {
    const payload: AdvancedStrategy<CookieConfig> = {
      type: TYPE,
      name: form.name.trim(),
      description: form.description.trim(),
      sites: form.sites,
      config: {
        protection_method: form.protection_method,
        compatibility_time: form.compatibility_time,
        cookie_names: form.cookie_names,
        enable_http_only: form.enable_http_only,
        enable_secure: form.enable_secure,
        enable_ip_check: form.enable_ip_check,
        action: form.action,
        block_value: form.block_value,
        delay: form.delay,
        redirect_value: form.action === 'Redirect' ? form.redirect_value.trim() : '',
      },
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton className={styles.skelForm} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Advanced Protection', slug: 'aiwaf/advanced' },
              { label: isEdit ? 'Edit Cookie Protection Rule' : 'Cookie Protection Rule' },
            ]}
          />
        }
        title="Cookie Protection Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>General</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Triggering Condition</CardTitle>
            </CardHeader>
            <CardBody>
            <div className={styles.fieldGridLg}>
              <Field
                label="Protection Mode"
                required
                description="Cookie Signature prevents cookies from being tampered with; Cookie Encryption prevents cookie contents from being leaked."
              >
                {() => (
                  <Select
                    value={form.protection_method}
                    onValueChange={(v) => set('protection_method', v)}
                  >
                    <SelectTrigger className={styles.selectLg}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROTECTION_MODES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field
                label="Start Time"
                required
                description="Before this time, requests with old cookies still pass and cookies are refreshed. After it, requests with old cookies are blocked until users clear them."
              >
                {(props) => (
                  <Input
                    {...props}
                    type="datetime-local"
                    step={1}
                    value={toLocalInput(form.compatibility_time)}
                    className={styles.dateInput}
                    onChange={(e) => set('compatibility_time', fromLocalInput(e.target.value))}
                  />
                )}
              </Field>

              <div>
                <div className={styles.subTitle}>
                  Security Attributes
                </div>
                <div className={styles.subDesc}>
                  HTTP Only: client JS cannot read the specified cookies. HTTPS: the specified
                  cookies are sent only over HTTPS.
                </div>
                <div className={styles.checkRow}>
                  <label className={styles.checkLabel}>
                    <Checkbox
                      checked={form.enable_http_only}
                      onCheckedChange={(v) => set('enable_http_only', !!v)}
                    />
                    <span>HTTP Only</span>
                  </label>
                  <label className={styles.checkLabel}>
                    <Checkbox
                      checked={form.enable_secure}
                      onCheckedChange={(v) => set('enable_secure', !!v)}
                    />
                    <span>HTTPS</span>
                  </label>
                </div>
              </div>

              <CheckboxField
                checked={form.enable_ip_check}
                onChange={(v) => set('enable_ip_check', v)}
                label="IP Verification"
                description="When enabled, the cookies below are bonded to the user's source IP; requests that don't match the cookie↔IP mapping are blocked."
              />

              <Field
                label="Cookie Name"
                required
                description="One cookie name per line. Enter * to match any cookie. Regex is not supported."
              >
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.cookie_names}
                      maxLength={COOKIE_MAX}
                      placeholder={'*\nsession_id'}
                      onChange={(e) => set('cookie_names', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.cookie_names.length}/{COOKIE_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Action</CardTitle>
              <CardDescription>Configure how requests that satisfy the triggering condition are handled.</CardDescription>
            </CardHeader>
            <CardBody>
            <div className={styles.actionGrid}>
              <Field label="Action" required>
                {() => (
                  <Select value={form.action} onValueChange={(v) => set('action', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map((a) => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <div className={styles.actionRow}>
                <Field label="Delay (second(s))" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={0}
                      value={String(form.delay)}
                      className={styles.numField}
                      onChange={(e) => set('delay', Number(e.target.value))}
                    />
                  )}
                </Field>
                {form.action === 'Block' && (
                  <Field label="Response Status Code" required>
                    {(props) => (
                      <Input
                        {...props}
                        type="number"
                        min={100}
                        max={599}
                        value={String(form.block_value)}
                        className={styles.numField}
                        onChange={(e) => set('block_value', Number(e.target.value))}
                      />
                    )}
                  </Field>
                )}
                {form.action === 'Redirect' && (
                  <Field label="Redirect Path" required>
                    {(props) => (
                      <Input
                        {...props}
                        value={form.redirect_value}
                        placeholder="/blocked"
                        className={styles.redirectInput}
                        onChange={(e) => set('redirect_value', e.target.value)}
                      />
                    )}
                  </Field>
                )}
              </div>
            </div>
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Apply to</CardTitle>
              <CardDescription>Select the websites this rule protects.</CardDescription>
            </CardHeader>
            <CardBody>
            {sites.length === 0 ? (
              <EmptyState icon={<Globe size={20} />} title="No websites available" description="Enable WAAP protection on a website first, then apply this rule." />
            ) : (
              <div className={styles.siteList}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.siteLabel}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
            </CardBody>
          </Card>

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/advanced?tab=cookie`)}>
              Cancel
            </Button>
            <Button disabled={!canSubmit} onClick={submit}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
    </Container>
  );
}
